import { CheckBadgeIcon } from "@heroicons/react/24/solid";
import { MagnifyingGlassCircleIcon } from '@heroicons/react/24/outline';
import type { MessageKindWithLink } from "@aidkitorg/aidkit/lib/application/communicate";
import { useContext, useEffect, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Button, DropdownMenu, Modal, useToast } from "@aidkitorg/component-library";
import { usePost } from "../API";
import InterfaceContext, { ConfigurationContext, PublicConfigurationContext, SupportedLanguage, UserInfoContext } from "../Context";
import { langToWord, useLocalizedStrings } from "../Localization";
import { SpacedSpinner, snakeToEnglish } from "../Util";
import { ClickableButton } from "./Button";
import { DateInput } from "./DateInput";
import { Dropdown } from "./Dropdown";
import { TextInput } from "./TextInput";
import { RichText } from "@aidkitorg/types/lib/survey";
import { useUserAllowed } from "./PermissionsUtil";
import { interfaceNumber } from "@aidkitorg/types/lib/survey";
import { sanitizePhoneNumberInput } from "../Util";

export function useApplicantSearchAllowed() {
  const publicConfig = useContext(PublicConfigurationContext);
  const allowedUsers = publicConfig?.capabilities?.applicantSearchConfig?.allowedUsers;
  const powerUsers = publicConfig?.capabilities?.powerSearch?.enabledForTags;
  const allUsers = [...allowedUsers ?? [], ...powerUsers ?? []]
  const allUsersAllowed = useUserAllowed(allUsers);
  const directRoleAuthority = useUserAllowed(['lookup', 'admin']);
  return allUsersAllowed || directRoleAuthority;
}

function usePowerSearchAllowed() {
  const publicConfig = useContext(PublicConfigurationContext);
  const powerUsers = publicConfig?.capabilities?.powerSearch?.enabledForTags;
  return useUserAllowed(powerUsers?.length ? powerUsers : ['admin']);
}

function AdminSearchForm(props: { closeModal: () => void } ) {
  const publicConfig = useContext(PublicConfigurationContext);

  const searchInputRef = useRef(null as any);
  const L = useLocalizedStrings();

  // These fields are searchable by admin to return N results
  const adminSearch = {
    "phone_number": "Phone",
    "email": "Email",
    "screener": "Screener",
    "legal_name": "Full Legal Name"
  }

  const history = useHistory();
  const context = useContext(InterfaceContext);

  const [term, setTerm] = useState("");
  const [field, setField] = useState("phone_number" as keyof typeof adminSearch);
  const search = usePost("/search");
  const [searching, setSearching] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [additionalFields, setAdditionalFields] = useState<string[]>([]);
    
  const getExposedSubsurveys = usePost("/applicant/get_exposed_subsurveys");
  const [gettingSubsurveys, setGettingSubsurveys] = useState(false);
  const [subsurveys, setSubsurveys] = useState([] as any[]);
  const [subsurveysApplicant, setSubsurveysApplicant] = useState("");
  const subsurveyLinks = useSubsurveyLinks({ uid: subsurveysApplicant, subsurveys });

  const doSearch = async () => {
    if (searching) return;
    setSearching(true);
    let result;
    try {
      result = await search({field: field, term});
    } catch (e) {
      console.warn("Search failed", e);
    } finally {
      setData(result?.data || []);
      setAdditionalFields(result?.additionalFields || []);
      setSearching(false);
    }
  }

  const doGetExposedSubsurveys = async (applicant: string) => {
    setGettingSubsurveys(true);
    let result = await getExposedSubsurveys({ applicant });
    setSubsurveysApplicant(applicant);
    setGettingSubsurveys(false);

    if (result && result.error) {
      console.warn("Failed to get subsurveys", result.error);
      return;
    }
    console.log("Setting subsurveys: ", result?.subsurveys);
    setSubsurveys(result?.subsurveys || []);
  }
    

  useEffect(() => {
    console.log("Search Ref: ", searchInputRef.current);
    if (searchInputRef.current) searchInputRef.current.focus();
  }, [searchInputRef.current]);
  
  const applicantPagePrefix = interfaceNumber?.(publicConfig.interface?.version) > 0 ? '/a' : '/applicant';

  return <>
    <h2>{L.applicant_lookup.admin_search}</h2>
    <div className="mt-2 flex flex-row space-x-2">
      <div className="">
        <Dropdown 
          label={adminSearch[field]}
          options={Object.keys(adminSearch).map((f) => ({
            label: adminSearch[f as keyof typeof adminSearch],
            callback: () => setField(f as keyof typeof adminSearch)
          }))}
          direction="right"
        />
      </div>
      <input
        aria-label={adminSearch[field]}
        className="flex grow border rounded-md p-2"
        value={term}
        onChange={(e) => {
          let sanitized = e.target.value || '';
          if (field === 'phone_number') {
            sanitized = sanitizePhoneNumberInput(sanitized);
          }
          setTerm(sanitized);
        }}
        onKeyUp={(e) => { if (e.key === "Enter") doSearch(); }}
        ref={searchInputRef} />

      <ClickableButton color="blue" disabled={searching}
        onClick={() => { doSearch() }}>
        {searching && <SpacedSpinner />}{L.dashboard.search}
      </ClickableButton>
    </div>

    <div className="mt-4">
      {searching && <div>{L.applicant_lookup.searching}</div>}
      {!searching && (
        <>
          {data.length === 0 && <div>{L.applicant_lookup.no_results_found}</div>}
          {data && data.length > 0 && (
            <table className="table-auto w-full">
              <thead>
                <tr key="search-head">
                  <th key="th1" className="px-1 pb-1 border border-top-0  border-left-0 border-right-0 border-bottom-2 border-gray-600 align-bottom">Name</th>
                  <th key="th2" className="px-1 pb-1 border border-top-0  border-left-0 border-right-0 border-bottom-2 border-gray-600 align-bottom">Stage</th>
                  <th key="th3" className="px-1 pb-1 border border-top-0  border-left-0 border-right-0 border-bottom-2 border-gray-600 align-bottom">Screener</th>
                  {additionalFields.map(field => (
                    <th
                      key={field}
                      className="px-1 pb-1 border border-top-0 border-left-0 border-right-0 border-bottom-2 border-gray-600 align-bottom"
                    >
                      {field.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}
                    </th>
                  ))}
                  <th key="th4" className={`px-1 pb-1 border border-top-0 border-left-0 border-right-0 border-bottom-2 border-gray-600 align-bottom`}>...</th>
                </tr>
              </thead>
              <tbody>

                {data.map(d => {
                  // display each row
                  return (
                    <>
                      <tr key={d.applicant} className="pt-2 my-1">
                        <td key={`${d.applicant}-name`} className="px-1 py-2 align-bottom"><Link className="text-[var(--ak-color-typography-link)]" to={`${applicantPagePrefix}/${d.applicant}`} onClick={() => props.closeModal()}>{d.name}</Link></td>
                        <td key={`${d.applicant}-stage`} className="px-1 py-2 align-bottom">{d.stage}</td>
                        <td key={`${d.applicant}-screener`} className="px-1 py-2 align-bottom">{d.screener}</td>
                        {additionalFields.map(field => (
                          <td key={`${d.applicant}-${field}`} className="px-1 py-2 align-bottom">
                            {d.additionalFields?.[field] ?? ''}
                          </td>
                        ))}
                        <td key={`${d.applicant}-action`} className={`px-1 py-2 align-bottom`}>
                          <ClickableButton color="indigo"
                            disabled={gettingSubsurveys}
                            onClick={() => doGetExposedSubsurveys(d.applicant)}>
                            {gettingSubsurveys && <SpacedSpinner />}{L.applicant_lookup.get_subsurveys}
                          </ClickableButton>
                        </td>
                      </tr>
                      {(!gettingSubsurveys && !!subsurveys.length && subsurveysApplicant === d.applicant) &&
                        <tr>
                          <td colSpan={4 + additionalFields.length}>{subsurveyLinks}</td>
                        </tr>
                      }
                    </>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  </>
}

/** Shared component for displaying subsurvey list with send/view actions */
export function SubsurveyList(props: {
  uid: string;
  subsurveys: {
    subsurveyNameTranslations: Record<string, string>;
    part: string;
    url?: string;
    send_link?: RichText;
  }[];
  showHeader?: boolean;
}) {
  const L = useLocalizedStrings();
  const context = useContext(InterfaceContext);
  const config = useContext(ConfigurationContext);
  const publicConfig = useContext(PublicConfigurationContext);
  const { toast } = useToast();

  const [sending, setSending] = useState(false);

  const names = new Intl.DisplayNames([context.lang], { type: "language" });

  const sendExposedSubsurvey = usePost('/applicant/send_secure_subsurvey');

  const sendLinkInLanguage = async (part: string, lang: string, kind: MessageKindWithLink) => {
    setSending(true);
    const result = await sendExposedSubsurvey({ part, lang: lang as SupportedLanguage, kind, applicant: props.uid });
    setSending(false);
    if (result && (result as any).status) {
      toast({
        description: JSON.stringify(result),
        variant: 'success'
      });
    }
  };

  if (!props.subsurveys.length) return <></>;

  return (
    <>
      {props.showHeader !== false && (
        <>
          <h3 className="text-lg font-medium leading-6 text-gray-900">{L.applicant_lookup.subsurveys}</h3>
          <p className="mt-2 max-w-4xl text-sm text-gray-600">
            {L.applicant_lookup.available_subsurveys}
          </p>
        </>
      )}
      {sending && (
        <>
          <SpacedSpinner />{L.applicant_lookup.sending}
        </>
      )}
      {!sending && (
        <ul role="list" className="">
          {props.subsurveys.map((subsurvey, i) => {
            return (
              <li
                key={subsurvey.part}
                className={`p-2 border border-left-0 border-right-0 border-gray-300
                  ${i !== 0 ? 'border-top-0' : ''}`}
              >
                <div className="flex items-center space-x-4">
                  <div className="min-w-10 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900 my-0 capitalize">
                      {subsurvey.subsurveyNameTranslations[context.lang]}
                    </p>
                    <p className="truncate text-sm text-gray-600 my-0 capitalize">{subsurvey.part}</p>
                  </div>
                  <div className="flex flex-1 items-center gap-2">
                    {subsurvey.send_link && (
                      <div className="p-2 flex flex-column gap-2">
                        <DropdownMenu
                          anchor="bottom"
                          icon="ChatBubbleOvalLeftEllipsisIcon"
                          label={L.applicant_lookup.send_via_sms || "Send via SMS"}
                          options={(config.languages as string || 'en').split(',').map(lang => {
                            return {
                              label: "Send SMS in " + (names.of(lang.trim().replace('_', '-')) ?? ''),
                              callback: async () => {
                                sendLinkInLanguage(subsurvey.part, lang, 'sms');
                              }
                            };
                          })}
                        />
                        {!!publicConfig.comms?.twilioConfig?.whatsapp_messaging_service_sid && (
                          <>
                            <DropdownMenu
                              anchor="bottom"
                              icon="ChatBubbleOvalLeftEllipsisIcon"
                              label={L.applicant_lookup.send_via_whatsapp}
                              options={(config.languages as string || 'en').split(',').map(lang => {
                                return {
                                  label: "Send WhatsApp message in " + (names.of(lang.trim().replace('_', '-')) ?? ''),
                                  callback: async () => {
                                    sendLinkInLanguage(subsurvey.part, lang, 'whatsapp');
                                  }
                                };
                              })}
                            />
                          </>
                        )}
                        <DropdownMenu
                          anchor="bottom"
                          icon="EnvelopeIcon"
                          label={L.applicant_lookup.send_via_email}
                          options={(config.languages as string || 'en').split(',').map(lang => {
                            return {
                              label: "Send Email in " + (names.of(lang.trim().replace('_', '-')) ?? ''),
                              callback: async () => {
                                sendLinkInLanguage(subsurvey.part, lang, 'email');
                              }
                            };
                          })}
                        />
                      </div>
                    )}
                    {subsurvey.url && (
                      <Button variant="link" href={subsurvey.url} className="ml-2" openInNewWindow={true}>
                        {L.applicant_lookup.view}
                      </Button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

function useSubsurveyLinks(props: {
  uid: string,
  subsurveys: {
    subsurveyNameTranslations: Record<string, string>,
    part: string,
    url?: string,
    send_link?: RichText,
  }[]
}) {
  if (!props.subsurveys.length) return <></>;

  return (
    <div className="border border-gray-200 bg-gray-100 rounded p-3 flow-root">
      <SubsurveyList uid={props.uid} subsurveys={props.subsurveys} showHeader={true} />
    </div>
  );
}

export function FoundApplicant(props: {
  verifiedSubsurveys: { 
    subsurveyNameTranslations: Record<string, string>, 
    part: string, 
    url?: string,
    send_link?: RichText,
  }[],
  uid: string,
  displayInfo: Record<string, string>
}) {
  const L = useLocalizedStrings();
  const subsurveyLinks = useSubsurveyLinks({ uid: props.uid, subsurveys: props.verifiedSubsurveys });

  return <div className="mb-4">
    <hr />
    <div className="m-2">
      {Object.keys(props.displayInfo).length ?
        <div><strong>{L.applicant_lookup.applicant_info}</strong></div> : <></>
      }
      {Object.keys(props.displayInfo).map(k => <div className='text-sm'>
        <strong>{snakeToEnglish(k)}:</strong> {props.displayInfo[k]}
      </div>)}
    </div>
    {props.verifiedSubsurveys.length > 0 && <>{subsurveyLinks}</>}
    {props.verifiedSubsurveys.length === 0 ? <p>{L.applicant_lookup.no_subsurveys_found}</p> : <></>}
  </div>
}

export function ProtectedSearchForm(props: { closeModal: () => void }) {

  const context = useContext(InterfaceContext);
  const L = useLocalizedStrings();
  const publicConfig = useContext(PublicConfigurationContext);

  const [allowPhone, allowEmail] = ((searchByContact) => {
    if (!searchByContact || searchByContact.kind === 'Email and Phone') return [true, true];
    if (searchByContact.kind === 'Phone Only') return [true, false];
    if (searchByContact.kind === 'Email Only') return [false, true];
    return [true, true]; 
  })(publicConfig?.capabilities?.applicantSearchConfig?.searchByContact)

  // could be subject to change
  type ProtectedSearchField = 'birth_date' | 'phone_number' | 'email';

  // protected search options are an explicit 2 item array
  // the two items are Birth date, and Contact, 
  // where contact is either Phone or Email (selectable)
  // it can also be configured in program config to be email only or phone only.
  type applicantSearch = [{ 
    kind: 'Date Field', 
    field: ProtectedSearchField, 
    label: string 
  }, { 
    kind: 'Dropdown', 
    options: {
      field: ProtectedSearchField, 
      label: string
    }[]
  } | { 
    kind: 'Contact Field',
    field: ProtectedSearchField,
    label: string
  }];

 
  const protectedSearch: applicantSearch = [{
    kind: 'Date Field',
    field: 'birth_date',
    label: L.applicant_lookup.date_of_birth
  }, (allowPhone && allowEmail) ? {
    kind: "Dropdown",
    options: [
      {
        field: 'phone_number' as const,
        label: L.submission.phone_number 
      },
      { 
        field: 'email' as const,
        label: L.applicant_lookup.email 
      }
    ]
  } : {
    kind: 'Contact Field',
    field: allowPhone ? 'phone_number' : 'email',
    label: allowPhone ? L.submission.phone_number : L.applicant_lookup.email
  }];

  // TODO: move to i18n
  const questions = {
    'street_number': { 
      'en': { 'question': `What is the street number of the applicant's home address?`, 'helptext': "Enter 'unhoused' if they do not have a home address or are currently unhoused." },
      'es': { 'question': '¿Cuál es el número de la calle de su domicilio?', 'helptext': "Ingrese 'unhoused' si está actualmente enfrente de la pobreza." },
      'zh_CN': { 'question': '你的住宅地址的街道号码是什么？', 'helptext': '如果你目前遭遇贫困，请输入“unhoused”。' },
      'yue': { 'question': '你的住宅地址的街道号码是什么？', 'helptext': '如果你目前遭遇贫困，请输入“unhoused”。' },
      'ar': { 'question': 'ما هي عنوان الشارع الخاص بك؟', 'helptext': 'إذا كنت تواجه الحالة المستعجلة، يرجى إدخال "unhoused" ' },
      'pl': { 'question': 'Jakie jest numer domu Twojej domówki?', 'helptext': 'Jeśli obecnie przeżywasz w wolności, wpisz "unhoused".' },
      'tl': { 'question': 'Ano ang iyong address?', 'helptext': "Ilagay ang 'unhoused' kung kasalukuyan kang nakakaranas ng kawalan ng tirahan." }
    }
  }

  const [terms, setTerms] = useState({} as Record<ProtectedSearchField, string>); // holds our search terms.    
  const [contactField, setContactField] = useState<ProtectedSearchField>(allowPhone ? 'phone_number' : 'email');
  
  const [searching, setSearching] = useState(false);

  const searchBegin = usePost('/protected_search', { suppressErrorDisplay: true });
  const searchComplete = usePost('/protected_search_answer');
    
    type SearchResult = Required<Awaited<ReturnType<typeof searchBegin>>>['result'];
    type Question = SearchResult['question'];

    const [searchResult, setSearchResult] = useState(undefined as undefined | SearchResult );
    const [language, setQuestionLanguage] = useState("en" as keyof typeof questions[Question]); // What language to ask the question in
    const [answer, setAnswer] = useState("");
    const [error, setError] = useState("");

    const [submitting, setSubmitting] = useState(false);
    const [foundApplicants, setFoundApplicants] = useState([] as Required<Awaited<ReturnType<typeof searchComplete>>>['results']);
    const [verified, setVerified] = useState(false);

    // Validation logic for required fields
    const isFormValid = () => {
      const birthDate = terms['birth_date'];
      const contactInfo = terms[contactField];
      
      // Both birth date and contact info must be provided and not empty
      return birthDate && birthDate.trim() !== '' && 
             contactInfo && contactInfo.trim() !== '';
    };

    // Get validation state for individual fields
    const getFieldValidationState = (field: ProtectedSearchField) => {
      const value = terms[field];
      const isEmpty = !value || value.trim() === '';
      const isRequired = field === 'birth_date' || field === contactField;
      
      return {
        isEmpty,
        isRequired,
        isValid: !isRequired || !isEmpty,
        hasError: isRequired && isEmpty
      };
    };

    const doSearch = async () => {
      // Prevent search if form is not valid
      if (!isFormValid()) {
        return;
      }

      setVerified(false);
      setSearching(true);
      try {
        setError("");
        const beginResult = await searchBegin({ terms, fields: ['birth_date', contactField] });
        if (beginResult?.result) { 
          console.log(beginResult.result);
          setSearchResult(beginResult.result);
        } else if (beginResult?.error) {
          setError(beginResult.error);
        }
      } catch (error) {
        console.error("Error during protected search:", error);
      } finally {
        setSearching(false);
      }
    }

    const answerSearch = async () => {
      console.log(answer);
      if (!searchResult) return;

      setSubmitting(true);
      const answerResult = await searchComplete({ questionTokens: searchResult.tokens, answer });
      if (answerResult && answerResult.results) {
        console.log(answerResult.results);
        setFoundApplicants(answerResult.results);
        setVerified(true);
      }
      setSubmitting(false);
    }

    return <>
      <h2>{L.applicant_lookup.title}</h2>
      {(!searchResult || !searchResult.tokens) && (<p className="text-gray-500 font-light">{L.applicant_lookup.tool_description}</p>)}
      <hr/>
      {(!searchResult || !searchResult['tokens']) && (
        <>
          {protectedSearch.map((searchField, i) => {
            return <div key={'search-' + searchField.kind} className="mt-2">
              {(() => {
                // Generate the Date Input
                if (searchField.kind === 'Date Field') {
                  const validation = getFieldValidationState(searchField.field);
                  return <DateInput key="birth_date" 
                    label={searchField.label + (validation.isRequired ? ' *' : '')}
                    setValue={(v) => {
                      setTerms((prevState) => {
                        const newState = {...prevState};
                        newState[searchField.field] = v;
                        return newState;
                      })
                    }} />
                }

                // Generate dropdown and Text input
                if (searchField.kind === 'Contact Field' || searchField.kind === 'Dropdown') {
                  const validation = getFieldValidationState(contactField);
                  const currentLabel = searchField.kind === 'Dropdown'
                    ? (searchField.options.find(f => f.field === contactField)?.label || searchField.options[0].label)
                    : searchField.label;

                  return (
                    <fieldset className="border-0 p-0 m-0">
                      <label className="font-bold">
                        {currentLabel + (validation.isRequired ? ' *' : '')}
                      </label>

                      <div className="flex flex-row space-x-2">
                        {searchField.kind === 'Dropdown' && (
                          <div>
                            <Dropdown
                              label={searchField.options.find(f => f.field === contactField)?.label || searchField.options[0].label}
                              options={searchField.options.map((opt) => ({
                                label: opt.label,
                                callback: () => setContactField(opt.field)
                              }))}
                              direction="right"
                            />
                          </div>
                        )}

                        <input
                          aria-label={currentLabel}
                          className="flex grow border rounded-md p-2"
                          value={terms[contactField] || ''}
                          onChange={(e) => {
                            let sanitized = e.target.value || '';
                            if (contactField === 'phone_number') {
                              sanitized = sanitizePhoneNumberInput(sanitized);
                            }
                            setTerms((prevState) => {
                              const newState = {...prevState};
                              newState[contactField] = sanitized;
                              return newState;
                            })
                          }}
                        />
                      </div>
                    </fieldset>
                  );
                }
              })()}
              <hr/>
            </div>
          })}
          <div className="mt-4 flex-row w-full flex-end">
            <ClickableButton 
              color="blue" 
              onClick={() => {
                doSearch()
              }}
              disabled={!isFormValid() || searching}
              extraClasses={!isFormValid() ? 'opacity-50 cursor-not-allowed' : ''}
            >
              {searching && <SpacedSpinner />}{L.dashboard.search}
            </ClickableButton>
          </div>
          {!isFormValid() && (
            <div className="mt-2 text-sm text-red-600">
              {L.applicant_lookup.fill_in_required_fields}
            </div>
          )}
        </>)}
      {error && <p className="my-2">{error}</p>}
      {!verified && searchResult && searchResult['tokens'] && (
        <>
          <div className="my-2">
            <Dropdown label={langToWord(language, 'Name')} 
              direction="right"
              options={Object.keys(questions[searchResult.question]).map((l) => {
                return {
                  label: l,
                  callback: () => setQuestionLanguage(l as keyof typeof questions[Question])
                }
              })} />
          </div>
          <TextInput value={answer} 
            label={questions['street_number'][language]['question'] || questions['street_number']['en']['question']}
            helptext={questions['street_number'][language]['helptext'] || questions['street_number']['en']['helptext']}
            onChange={(e) => { 
              setAnswer(e.target.value);
            }} />
          <div className="mt-4 flex-row w-full flex-end">
            <ClickableButton color="green" onClick={() => {
              answerSearch()
            }}>{submitting && <SpacedSpinner />}{L.submission.submit}</ClickableButton>
          </div>
        </>
      )}
      {verified && <div className="mt-4 flex flex-row">
        <CheckBadgeIcon className="h-7 text-blue-500 font-medium my-auto" />&nbsp;<span className="text-gray-800 font-bold">
          {foundApplicants.length > 1 ? L.applicant_lookup.verified_applicants : L.applicant_lookup.verified_applicant}
        </span>
      </div>}
      {verified && 
            foundApplicants.map(a => <FoundApplicant uid={a.uid} verifiedSubsurveys={a.subsurveys} displayInfo={a.displayInfo} />)
      }
    </>
}

function SearchModal(props: { open: boolean, close: () => void }) {
  const L = useLocalizedStrings();
  const config = useContext(ConfigurationContext);
  const user = useContext(UserInfoContext);
  const powerSearchAllowed = usePowerSearchAllowed();

  //
  // Set the local "isAdmin" flag for search if the user is an admin, OR if they
  // are included in the list of powerSearch users in capabilities config.
  //
  const isAdmin = (config.roles || '').includes('admin') || user.tags?.includes('admin') || powerSearchAllowed;

  const [searchForm, setSearchForm] = useState('protected' as 'protected' | 'admin');

  return (
    <Modal
      open={props.open}
      onClose={() => props.close()}
      title=""
      size="lg"
      className="min-h-[550px] max-w-4xl"
    >
      <div>
        <div className="sm:hidden">
          <label htmlFor="tabs" className="sr-only">{L.applicant_lookup.select_search_type}</label>
          <select id="tabs" name="tabs"
            className="block w-full focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md">
            <option selected>{L.applicant_lookup.protected_search}</option>
            <option>{L.applicant_lookup.admin_search}</option>
          </select>
        </div>
        <div className="hidden sm:block">
          <nav className="flex space-x-4" aria-label="Tabs">
            <button type="button" onClick={() => setSearchForm("protected")} aria-current="page"
              className={`border-0 ${searchForm === 'protected' ? 'bg-indigo-100 text-indigo-700' : 'border-gray-100 bg-gray-200 text-gray-700 hover:text-white hover:bg-gray-600'}
                                            px-3 py-2 font-medium text-sm rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600`}>{L.applicant_lookup.protected_search}</button>
            {isAdmin ? <button type="button" onClick={() => setSearchForm("admin")}
              className={`border-0 ${searchForm === 'admin' ? 'bg-indigo-100 text-indigo-700' : 'border-gray-100 bg-gray-200 text-gray-700 hover:text-white hover:bg-gray-600'}
                                         px-3 py-2 font-medium text-sm rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600`}>{L.applicant_lookup.admin_search}</button> : <></>}
          </nav>
        </div>
      </div>
      <div className="mt-4">
        {searchForm === 'protected' && <ProtectedSearchForm closeModal={props.close} />}
        {searchForm === 'admin' && <AdminSearchForm closeModal={props.close} />}
      </div>
    </Modal>
  );
}

/** Search Bar! 
 * Appends to the main NavBar for reviewers and screeners
 * You can search by //, phone number, email (exact values only).
 * Pulls up all applicants matching the criteria.
 * All in a mobile friendly container.
 */
export function SearchBar(props: {
  newInterface?: boolean
}) {
  const [showInput, setShowInput] = useState(false);
  const L = useLocalizedStrings();

  return (
    <>
      {props.newInterface ? 
        <div 
          className={'border-gray-200 text-gray-600 hover:text-indigo-600 mt-3'}>
          <a
            tabIndex={0}
            className='ml-1 pl-2 text-gray-600 hover:text-indigo-600 font-medium cursor-pointer flex flex-row'
            key={'searchApplicants'}
            onClick={() => setShowInput(true)}  >
            <MagnifyingGlassCircleIcon className="mt-1 mr-1 w-5 h5"
              style={{ cursor: 'pointer' }} >
            </MagnifyingGlassCircleIcon>
            {L.applicant_lookup.title}
          </a> 
        </div> :
        <MagnifyingGlassCircleIcon className='h-8 w-8 mx-1 my-auto'
          style={{ cursor: 'pointer' }}
          onClick={() => setShowInput(true)} >
        </MagnifyingGlassCircleIcon>
      }
      <SearchModal open={showInput} close={() => setShowInput(false)} />
    </>
  );

}
